import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { SectionLoadError } from "./SectionErrorBoundary";
import { useCommissionKpis } from "@/hooks/useInvoices";
import { useGoals } from "@/hooks/useGoals";
import { useQueryClient } from "@tanstack/react-query";
import { StrategicSupplierGauge } from "./StrategicSupplierGauge";
import { GoalProgressBar } from "@/components/goals/GoalProgressBar";
import { formatKpiCurrency } from "@/lib/formats/currency";
import type { KpiCardKey } from "@shared/kpi-basis";

function formatKpi(value: string | undefined): string {
  return formatKpiCurrency(parseFloat(value ?? "0"));
}

interface CommissionKpi {
  basisKey: KpiCardKey;
  label: string;
  value: string;
  tooltip: string;
}

/* AR-316 — the v9 Finances card anatomy (migration-docs/v9): an outlined
   surface carrying an uppercase label, the figure, and a short line saying what
   the figure counts. v9 uses the same anatomy at every width; only the column
   count changes.

   The "what it counts" line is `KpiBasisLabel`, which reads the same
   shared/kpi-basis.ts table the server routes on — so the card cannot claim a
   basis the query isn't using.

   Module scope, not nested in CommissionKpis: a nested definition is a new
   function identity on every render, so React remounts the card whenever a
   query settles and `InfoHint` loses its open state mid-read. */
function KpiCard({ kpi }: { kpi: CommissionKpi }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-1.5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {kpi.label}
        </p>
        <InfoHint label={`How ${kpi.label} is calculated`}>
          <p>{kpi.tooltip}</p>
          <KpiBasisNote card={kpi.basisKey} />
        </InfoHint>
      </div>
      <p className="mt-1.5 text-3xl font-bold tracking-tight tabular-nums">{kpi.value}</p>
      <KpiBasisLabel card={kpi.basisKey} className="mt-1 text-xs" />
    </div>
  );
}

interface CommissionKpisProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
}

export function CommissionKpis({ tramsAccountId, viewMode }: CommissionKpisProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const { data, isLoading, error } = useCommissionKpis({ tramsAccountId, viewMode });
  const { data: goalsData, isLoading: goalsLoading } = useGoals(currentYear);

  if (error) {
    return (
      <SectionLoadError
        sectionName="Commission Performance"
        error={error}
        onRetry={() =>
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/commission-kpis"] })
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div aria-label="Loading commission performance" className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Commission Performance (YTD)</h2>
        <div className="flex gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border bg-card p-5">
                <Skeleton className="h-4 w-28 mb-3" />
                <Skeleton className="h-8 w-40" />
              </div>
            ))}
          </div>
          <div className="hidden lg:flex items-center justify-center min-w-[180px]">
            <Skeleton className="h-[80px] w-[140px] rounded-t-full" />
          </div>
        </div>
      </div>
    );
  }

  // Three periods, three populations, three sources. Outstanding is not Invoiced minus
  // Paid, so each card carries its basis from shared/kpi-basis.ts.
  const kpis: CommissionKpi[] = [
    {
      basisKey: "invoicedCommission",
      label: "Invoiced Gross Commission",
      value: formatKpi(data?.invoicedYtd),
      tooltip:
        "Total gross commission on all invoices, open and closed, with a return date this year. This is what you've booked, not what's been paid. Gross means the full commission before your agency split.",
    },
    {
      basisKey: "paidCommission",
      label: "Paid Gross Commission",
      value: formatKpi(data?.paidYtd),
      tooltip:
        "Gross commission from closed invoices paid to you this calendar year, by statement date. Gross is before your agency split.",
    },
    {
      basisKey: "outstandingCommission",
      label: "Open Gross Commission",
      value: formatKpi(data?.outstandingYtd),
      tooltip:
        "Gross commission on open (unpaid) invoices with a return date this year. Gross means the full commission before your agency split.",
    },
  ];

  const hasTeamAccess = goalsData?.showTeamByDefault ?? false;
  const goalScope: "team" | "personal" =
    viewMode === "all" && hasTeamAccess ? "team" : "personal";
  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const commissionGoalAmount = parseFloat(activeGoal?.commissionGoal ?? "0");
  const paidAmount = parseFloat(data?.paidYtd ?? "0");
  const invoicedAmount = parseFloat(data?.invoicedYtd ?? "0");

  return (
    <section aria-labelledby="commission-kpis-heading">
      <div className="mb-3 space-y-0.5">
        {/* AR-316 — v9 section header: the uppercase label, then a rule that
            runs out to the edge of the section. */}
        <div className="flex items-center gap-3">
          <h2
            id="commission-kpis-heading"
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            Commission Performance (YTD)
          </h2>
          <span aria-hidden className="h-px flex-1 bg-border" />
        </div>
        {/*
          The three cards sit next to each other on three different periods, so a user
          will subtract them. Outstanding is not Invoiced minus Paid: each card counts a
          different population. The line under the heading says so once, so the cards
          can carry their own basis without repeating the caveat.
        */}
        <p className="text-[11px] font-normal normal-case text-muted-foreground/80">
          Three populations, three periods — the cards below are not additive.
        </p>
      </div>
      {/* Desktop: 4-col grid — KPIs span cols 1-3 row 1, progress bar spans cols 1-3 row 2, gauge spans col 4 rows 1-2 */}
      <div className="hidden lg:grid lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
        <div className="row-span-2 flex items-center justify-center rounded-xl border bg-card/50">
          <StrategicSupplierGauge tramsAccountId={tramsAccountId} viewMode={viewMode} />
        </div>
        {!goalsLoading && (
          <div className="col-span-3">
            <GoalProgressBar
              className="rounded-xl border bg-card/50 px-4 py-3 h-full"
              goalAmount={commissionGoalAmount}
              paidAmount={paidAmount}
              invoicedAmount={invoicedAmount}
            />
          </div>
        )}
      </div>

      {/* Mobile / tablet: stacked layout */}
      <div className="lg:hidden space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {kpis.map((kpi) => (
            <KpiCard key={kpi.label} kpi={kpi} />
          ))}
        </div>
        {!goalsLoading && (
          <GoalProgressBar
            className="rounded-xl border bg-card/50 px-4 py-3"
            goalAmount={commissionGoalAmount}
            paidAmount={paidAmount}
            invoicedAmount={invoicedAmount}
          />
        )}
        <div className="flex justify-center rounded-xl border bg-card/50 py-3">
          <StrategicSupplierGauge tramsAccountId={tramsAccountId} viewMode={viewMode} />
        </div>
      </div>
    </section>
  );
}
